import { useEffect, type CSSProperties, type ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router';
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react';
import {
  Avatar,
  Button,
  EmptyState,
  IllustrationEmptyBox,
  IllustrationOffline,
  List,
  ListItem,
  Skeleton,
  cn,
  toast,
  useHotkey,
  useIsDesktop,
} from '@dezza/ui';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { isNativeMobile } from '../lib/platform';
import { useAuth } from '../lib/auth';
import { useRealtime } from '../lib/realtime';
import { UpgradeProvider } from '../lib/access';
import { Banners } from './Banners';
import { BottomNav, BOTTOM_NAV_HEIGHT, useTyping } from './BottomNav';
import { CommandMenu } from './CommandMenu';
import { isTopLevelPath } from './nav';
import { NavSheet, Sidebar } from './SideMenu';
import { useShell } from './shell-store';
import { useOfflineSync } from '../features/pos/offline';

const SIDEBAR = 256;
const RAIL = 72;

/**
 * Entrada suave a cada troca de tela: só opacidade (160 ms), nunca segura o conteúdo.
 * Sem transform de propósito: transform criaria um novo contexto para as barras fixas (FloatingBar).
 */
function RouteFade({ children }: { children: ReactNode }) {
  const matches = useMatches();
  const id = matches[matches.length - 1]?.id ?? '';
  return (
    <div key={id} className="animate-fade-in motion-reduce:animate-none">
      {children}
    </div>
  );
}

/**
 * Layout autenticado. Desktop: menu lateral de 256 px que recolhe para um trilho de 72 px (tecla [ ).
 * Celular: barra de abas no rodapé (telas de primeiro nível) e folha "Mais" com a navegação completa.
 * Paleta de comandos global (Ctrl/Cmd + K ou /).
 */
export function AppShell() {
  const isDesktop = useIsDesktop();
  const collapsed = useShell((s) => s.collapsed);
  const toggleCollapsed = useShell((s) => s.toggleCollapsed);
  const setMenuOpen = useShell((s) => s.setMenuOpen);
  const location = useLocation();
  const typing = useTyping();
  const { me } = useAuth();
  useRealtime();
  useOfflineSync();

  useEffect(() => setMenuOpen(false), [location.pathname, setMenuOpen]);
  useHotkey('[', () => toggleCollapsed(), { enabled: isDesktop });

  const width = isDesktop ? (collapsed ? RAIL : SIDEBAR) : 0;
  const tabs = !isDesktop && isTopLevelPath(location.pathname);
  // O botão "Vender" sobe 20 px acima da barra de abas: barras e botões flutuantes da tela ficam acima dele.
  const raise = me?.membership?.permissions.includes('sales.create') ? '1.25rem' : '0px';
  const style = {
    '--sidebar-width': `${width}px`,
    ...(tabs && !typing
      ? {
          '--bottom-nav-offset': `calc(${BOTTOM_NAV_HEIGHT} + env(safe-area-inset-bottom))`,
          '--bottom-nav-safe': raise,
          '--bottom-nav-raise': raise,
        }
      : {}),
  } as CSSProperties;

  return (
    <UpgradeProvider>
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-control focus:bg-elevated focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-ink focus:shadow-pop"
      >
        Pular para o conteúdo
      </a>
      <div className="min-h-dvh" style={style}>
        {isDesktop ? (
          <aside
            className="fixed inset-y-0 left-0 z-40 transition-[width] duration-200 ease-snappy motion-reduce:transition-none"
            style={{ width }}
          >
            <Sidebar />
          </aside>
        ) : (
          <NavSheet />
        )}
        {/* overflow-x-clip: um excesso lateral numa tela não alarga a página (no celular isso deslocaria as barras fixas). Não quebra o sticky. */}
        <div
          className="relative overflow-x-clip transition-[padding] duration-200 ease-snappy motion-reduce:transition-none"
          style={{ paddingLeft: width }}
        >
          <Banners />
          <main
            id="conteudo"
            tabIndex={-1}
            className={cn('min-h-dvh outline-none', tabs && 'pb-[calc(4rem+env(safe-area-inset-bottom))]')}
          >
            <RouteFade>
              <Outlet />
            </RouteFade>
          </main>
          {isNativeMobile ? null : <PwaUpdater />}
        </div>
        {tabs && !typing ? <BottomNav /> : null}
        <CommandMenu />
      </div>
    </UpgradeProvider>
  );
}

/** Esqueleto do app inteiro enquanto a sessão é conferida (no lugar do indicador girando). */
export function AppSkeleton() {
  const isDesktop = useIsDesktop();
  return (
    <div className="min-h-dvh" role="status" aria-label="Abrindo sua loja">
      {isDesktop ? (
        <div className="fixed inset-y-0 left-0 w-64 border-r border-line bg-surface p-3">
          <div className="flex items-center gap-2.5 p-2">
            <Skeleton className="size-[38px] rounded-[11px]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <div className="mt-5 space-y-2 px-2">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="flex h-9 items-center gap-3">
                <Skeleton className="size-6 rounded-lg" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div style={{ paddingLeft: isDesktop ? 256 : 0 }}>
        <div
          className={cn(
            'flex h-14 items-center gap-3 px-4',
            isDesktop ? 'border-b border-line bg-surface' : 'bg-dark',
          )}
        >
          <Skeleton className={cn('h-4 w-28', !isDesktop && 'bg-white/10')} />
        </div>
        <div className="mx-auto max-w-7xl space-y-4 px-4 py-6 sm:px-6">
          <Skeleton className="h-9 w-64" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-card" />
            ))}
          </div>
          <Skeleton className="h-72 rounded-card" />
        </div>
      </div>
      <span className="sr-only">Abrindo sua loja...</span>
    </div>
  );
}

/** Rotas protegidas: exige sessão e loja ativa. */
export function RequireAuth() {
  const { me, isLoading, unreachable, logout, switchCompany } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  if (isLoading && !me) return <AppSkeleton />;
  // Sem resposta da API (internet ou servidor): avisa e tenta de novo, em vez de mandar para o login.
  if (!me && unreachable)
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <EmptyState
          illustration={<IllustrationOffline />}
          title="Não conseguimos falar com o Dezza"
          description="Confira a internet e tente de novo em alguns segundos."
          actions={
            <Button leftIcon={<RefreshCw />} onClick={() => window.location.reload()}>
              Tentar de novo
            </Button>
          }
        />
      </div>
    );
  if (!me)
    return (
      <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />
    );
  if (!me.company) {
    return (
      <div className="mx-auto max-w-md px-4 py-10">
        <EmptyState
          illustration={<IllustrationEmptyBox />}
          title={me.companies.length ? 'Escolha uma loja' : 'Você não tem acesso a nenhuma loja'}
          description={
            me.companies.length
              ? 'Selecione a loja que deseja abrir.'
              : 'Seu acesso pode ter sido removido. Peça um novo convite ao responsável pela loja.'
          }
        />
        {me.companies.length ? (
          <List inset className="mt-2">
            {me.companies.map((c) => (
              <ListItem
                key={c.id}
                leading={<Avatar name={c.name} src={c.logoUrl} size={36} />}
                title={c.name}
                onClick={() => void switchCompany(c.id)}
              />
            ))}
          </List>
        ) : null}
        <Button
          variant="ghost"
          block
          className="mt-4"
          leftIcon={<LogOut />}
          onClick={() => void logout().then(() => navigate('/entrar'))}
        >
          Sair
        </Button>
      </div>
    );
  }
  if (me.company.status !== 'active' && !location.pathname.startsWith('/configuracoes/plano')) {
    return (
      <div className="mx-auto max-w-md px-4 py-10">
        <EmptyState
          illustration={<IllustrationEmptyBox />}
          title={me.company.status === 'suspended' ? 'Loja suspensa' : 'Loja encerrada'}
          description={
            me.company.suspendedReason ?? 'Regularize a assinatura para voltar a usar todos os recursos.'
          }
          actions={
            me.membership?.permissions.includes('billing.manage') ? (
              <Button onClick={() => navigate('/configuracoes/plano')}>Ver assinatura</Button>
            ) : null
          }
        />
      </div>
    );
  }
  return <Outlet />;
}

/**
 * Aviso de nova versão do app (PWA). No app mobile nativo os arquivos já vêm no pacote
 * (atualização pela loja de apps): lá o service worker não é registrado.
 */
function PwaUpdater() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({ immediate: true });
  useEffect(() => {
    if (!needRefresh) return;
    toast('Nova versão disponível', {
      description: 'Atualize para receber as melhorias.',
      duration: Infinity,
      action: { label: 'Atualizar', onClick: () => void updateServiceWorker(true) },
    });
  }, [needRefresh, updateServiceWorker]);
  return null;
}

/** Erro inesperado numa rota: tela amigável com opção de recarregar. */
export function RouteError() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <EmptyState
        illustration={<AlertTriangle className="mx-auto size-16 text-warning" />}
        title={notFound ? 'Página não encontrada' : 'Algo deu errado'}
        description={
          notFound
            ? 'O endereço pode ter mudado.'
            : 'Tivemos um problema ao abrir esta tela. Tente recarregar.'
        }
        actions={
          <>
            <Button variant="outline" onClick={() => window.location.assign('/inicio')}>
              Ir para o início
            </Button>
            <Button leftIcon={<RefreshCw />} onClick={() => window.location.reload()}>
              Recarregar
            </Button>
          </>
        }
      />
    </div>
  );
}
